import { Component, DestroyRef, inject, OnInit, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEnvelope, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { KENDO_GRID } from '@progress/kendo-angular-grid';

import { NonProfileUser } from 'src/app/common/NonProfileUser';
import { AppProfileService } from 'src/app/services/profile.service';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { finalize, Observable, tap } from 'rxjs';
import { GridPdfColumnsDirective } from '../grid/grid-pdf-columns.directive';
import { PdfPageComponent } from '../grid/pdf-page.component';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

@Component({
    imports: [KENDO_GRID, FontAwesomeModule, AsyncClickDirective, GridPdfColumnsDirective, PdfPageComponent, GridNoRecordsComponent],
    selector: 'app-non-confirmed-users',
    templateUrl: './non-confirmed-users.component.html',
    styleUrls: ['./non-confirmed-users.component.css']
})
export class NonConfirmedUsersComponent implements OnInit {
  private profileService = inject(AppProfileService);
  private callApiService = inject(CallApiService);

  private destroyRef = inject(DestroyRef);

  readonly mode = input<'primary' | 'secondary'>('primary');

  public faTrashAlt = faTrashAlt;
  public faEnvelope = faEnvelope;
  public readonly expiredLabel = $localize`Abgelaufen`;
  public readonly validLabel = $localize`Gültig`;
  // Drives the grid's "no records" template: while true the centered spinner
  // shows instead of "Keine Daten vorhanden". Starts true because the list is
  // fetched on init.
  public loading: boolean = true;
  nonProfileUsers: NonProfileUser[];

  ngOnInit(): void {
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refresh();
    });
  }

  // Per-row factory handlers so [asyncClick] can guard each grid action against a
  // double-click. Resend and delete on different rows are independent (no lockGroup).
  public onResendConfirmationEMail(nonProfilUserSubject: string): () => Observable<unknown> {
    return () => {
      const resend$ = this.mode() === 'primary'
        ? this.profileService.resendPrimaryUserEmailConfirmation(nonProfilUserSubject)
        : this.profileService.resendSecondaryUserEmailConfirmation(nonProfilUserSubject);

      return this.callApiService.callWithToasts$(
        resend$,
        $localize`Die E-Mail zur Bestätigung der E-Mail-Adresse wurde versandt.`,
        $localize`Die E-Mail zur Bestätigung der E-Mail-Adresse konnte nicht versandt werden.`
      );
    };
  }

  onDelete(id: string): () => Observable<unknown> {
    return () => {
      const delete$ = this.mode() === 'primary'
        ? this.profileService.deleteNonConfirmedPrimaryUser(id)
        : this.profileService.deleteNonConfirmedSecondaryUser(id);

      return this.callApiService
        .confirmAndCallWithToasts$(
          delete$,
          $localize`Sind Sie sicher, dass Sie die offene Registrierung löschen möchten?`,
          $localize`Die offene Registrierung wurde gelöscht.`,
          $localize`Beim Löschen der offenen Registrierung ist ein Fehler aufgetreten.`
        )
        .pipe(tap(() => this.refresh()));
    };
  }

  refresh() {
    this.loading = true;
    const list$ = (this.mode() === 'primary'
      ? this.profileService.getNonConfirmedPrimaryUsers()
      : this.profileService.getNonConfirmedSecondaryUsers()
    ).pipe(finalize(() => (this.loading = false)));

    this.callApiService.callWithToasts(
      list$,
      null,
      $localize`Die Liste der offenen Anfragen konnte nicht geladen werden.`,
      (npu) => {
        const now = Date.now();
        npu.forEach((u) => {
          u.securityCodeExpirationDate = new Date(u.securityCodeExpirationDate);
          u.isExpired = u.securityCodeExpirationDate.getTime() <= now;
        });
        this.nonProfileUsers = npu;
      }
    );
  }
}
